import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { Pencil, Plus, Trash2 } from 'lucide-react'

import type { ShippingCarrier } from '@/lib/api/shipping-carriers-api'
import { PageBackButton } from '@/components/common/PageBackButton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import {
    useCreateShippingCarrier,
    useDeleteShippingCarrier,
    useShippingCarriers,
    useUpdateShippingCarrier,
} from '@/hooks/use-shipping-carriers-query'
import { m } from '@/lib/paraglide/messages'
import { createShippingCarrierSchema } from '@/lib/schemas/shipping-carrier-schemas'
import { DEFAULT_SHIPPING_CARRIER_ID } from '@/lib/constants'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/logistics/shipping-carriers',
)({
    component: ShippingCarriersPage,
})

type FormValues = {
    name: string
    is_active: boolean
    is_external: boolean
}

function ShippingCarriersPage() {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('shipping_carriers.write')
    const [editing, setEditing] = useState<ShippingCarrier | null>(null)
    const [open, setOpen] = useState(false)

    const { data } = useShippingCarriers({ per_page: 100 })
    const rows = data?.data ?? []

    const createMut = useCreateShippingCarrier()
    const updateMut = useUpdateShippingCarrier(editing?.id ?? '')
    const deleteMut = useDeleteShippingCarrier()

    return (
        <div className="space-y-4 page-pad">
            <div className="flex items-center justify-between">
                <div className="flex min-w-0 items-center gap-2">
                    <PageBackButton className="-my-2 md:-my-1" />
                    <h1 className="text-xl font-semibold">
                        {m.shipping_carrier_title()}
                    </h1>
                </div>
                {canWrite && (
                    <Button
                        size="sm"
                        onClick={() => {
                            setEditing(null)
                            setOpen(true)
                        }}
                    >
                        <Plus className="size-4" /> {m.shipping_carrier_new()}
                    </Button>
                )}
            </div>

            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>{m.shipping_carrier_name()}</TableHead>
                        <TableHead>{m.shipping_carrier_active()}</TableHead>
                        <TableHead>
                            {m.shipping_carrier_is_external()}
                        </TableHead>
                        <TableHead />
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow key={row.id}>
                            <TableCell>{row.name}</TableCell>
                            <TableCell>
                                <Badge
                                    variant={
                                        row.is_active ? 'default' : 'secondary'
                                    }
                                >
                                    {row.is_active ? '✓' : '—'}
                                </Badge>
                            </TableCell>
                            <TableCell>
                                <Badge
                                    variant={
                                        row.is_external
                                            ? 'default'
                                            : 'secondary'
                                    }
                                >
                                    {row.is_external ? '✓' : '—'}
                                </Badge>
                            </TableCell>
                            <TableCell className="text-right space-x-1">
                                {canWrite && (
                                    <>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => {
                                                setEditing(row)
                                                setOpen(true)
                                            }}
                                        >
                                            <Pencil className="size-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            disabled={
                                                row.id ===
                                                DEFAULT_SHIPPING_CARRIER_ID
                                            }
                                            onClick={() => {
                                                if (
                                                    window.confirm(
                                                        m.shipping_carrier_delete_confirm(),
                                                    )
                                                ) {
                                                    deleteMut.mutate(row.id)
                                                }
                                            }}
                                        >
                                            <Trash2 className="size-4" />
                                        </Button>
                                    </>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {editing
                                ? m.shipping_carrier_edit()
                                : m.shipping_carrier_new()}
                        </DialogTitle>
                    </DialogHeader>
                    <ShippingCarrierForm
                        initial={editing}
                        onSubmit={async (values) => {
                            try {
                                if (editing) {
                                    await updateMut.mutateAsync(values)
                                } else {
                                    await createMut.mutateAsync(values)
                                }
                                setOpen(false)
                            } catch {
                                // onError handler in the mutation hook surfaces the toast;
                                // dialog stays open so the user can correct the input.
                            }
                        }}
                        submitting={createMut.isPending || updateMut.isPending}
                    />
                </DialogContent>
            </Dialog>
        </div>
    )
}

function ShippingCarrierForm({
    initial,
    onSubmit,
    submitting,
}: {
    initial: ShippingCarrier | null
    onSubmit: (v: FormValues) => Promise<void>
    submitting: boolean
}) {
    const schema = createShippingCarrierSchema()
    const form = useForm<FormValues>({
        resolver: zodResolver(schema),
        defaultValues: {
            name: initial?.name ?? '',
            is_active: initial?.is_active ?? true,
            is_external: initial?.is_external ?? false,
        },
    })
    // Đơn vị mặc định "Tự giao" phải giữ is_external=false — BE cũng chặn,
    // nhưng FE disable switch cho rõ ràng.
    const isDefault = initial?.id === DEFAULT_SHIPPING_CARRIER_ID
    return (
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
            <div>
                <Label>{m.shipping_carrier_name()}</Label>
                <Input {...form.register('name')} />
                {form.formState.errors.name && (
                    <p className="text-xs text-red-500">
                        {form.formState.errors.name.message}
                    </p>
                )}
            </div>
            <div className="flex items-center gap-2">
                <Switch
                    checked={form.watch('is_active')}
                    onCheckedChange={(v) => form.setValue('is_active', v)}
                />
                <Label>{m.shipping_carrier_active()}</Label>
            </div>
            <div className="flex items-center gap-2">
                <Switch
                    checked={form.watch('is_external')}
                    disabled={isDefault}
                    onCheckedChange={(v) => form.setValue('is_external', v)}
                />
                <Label>{m.shipping_carrier_is_external()}</Label>
            </div>
            <p className="text-xs text-muted-foreground">
                {m.shipping_carrier_is_external_hint()}
            </p>
            <DialogFooter>
                <Button type="submit" disabled={submitting}>
                    {submitting ? '...' : m.shipping_carrier_new()}
                </Button>
            </DialogFooter>
        </form>
    )
}
